/* eslint-disable no-underscore-dangle */
import lod_ from "lodash";
import {
	Card,
	Chip,
	CircularProgress,
	Drawer,
	Icon,
	IconButton,
	Tab,
	Tabs,
	Tooltip
} from "@mui/material";
import MDBox from "components/Basics/MDBox";
import MDTypography from "components/Basics/MDTypography";
import { useEffect, useState } from "react";
import resourceActions from "redux-react/actions/resourceActions";
import { useDispatch } from "react-redux";
import Analyse from "./Analyse";
import { queryDictionary } from "helpers/dictionary";
import { createDictionarySkeleton } from "helpers/form";
import ConversationTab from "./ConversationTab";
import TabContent from "./TabContent";

/**
 * Format a date to a readable string (dd/MM/yyyy HH:mm).
 * @param {string|Object} date - The date to format.
 * @returns {string} The formatted date string.
 */
const formatDate = date => {
	if (lod_.isNil(date)) return "";
	const d = date?.$date ? new Date(date.$date) : new Date(date);
	if (Number.isNaN(d.getTime())) return "";
	return d.toLocaleDateString("fr-FR", {
		day: "2-digit",
		month: "2-digit",
		year: "numeric",
		hour: "2-digit",
		minute: "2-digit"
	});
};

/**
 * MetadataPill — small info pill displayed in the conversation header.
 * @param {string} icon - The MUI icon name.
 * @param {string} label - The label text.
 * @returns {JSX.Element} The rendered pill.
 */
const MetadataPill = ({ icon, label }) => (
	<Chip
		size="small"
		icon={<Icon sx={{ fontSize: "14px !important" }}>{icon}</Icon>}
		label={label}
		variant="outlined"
		sx={{
			borderColor: "#e0e0e0",
			color: "#7b809a",
			fontSize: "0.72rem",
			height: 26,
			"& .MuiChip-icon": { color: "#9e9e9e" }
		}}
	/>
);

/**
 * Build drawer paper styles based on fullscreen mode.
 * @param {boolean} fullScreen - Whether fullscreen is active.
 * @returns {Object} The sx styles for the Drawer.
 */
const drawerStyles = fullScreen => ({
	"& .MuiDrawer-paper": {
		width: fullScreen ? "100vw" : "42vw",
		minWidth: fullScreen ? "100vw" : 420,
		height: "100vh",
		display: "flex",
		flexDirection: "column",
		overflow: "hidden",
		margin: 0,
		boxSizing: "border-box",
		backgroundColor: "#ffffff",
		borderRadius: fullScreen ? 0 : "12px 0 0 12px",
		maxWidth: "100vw",
		padding: 0,
		boxShadow: "-4px 0 24px rgba(0,0,0,0.08)"
	}
});

/**
 * Analyse state badge color mapping.
 */
const ANALYSE_STATE_COLORS = {
	created: { bg: "#e3f2fd", color: "#1565c0" },
	pending: { bg: "#fff3e0", color: "#e65100" },
	completed: { bg: "#e8f5e9", color: "#2e7d32" },
	error: { bg: "#fce4ec", color: "#c62828" }
};

/**
 * ConversationLight component to display a conversation in a drawer.
 * @param {boolean} open - Whether the drawer is open.
 * @param {function} onClose - Function to close the drawer.
 * @param {Object} action - The action configuration.
 * @param {Object} values - The values related to the conversation.
 * @param {Object} resource - The resource configuration.
 * @return {JSX.Element} The rendered ConversationLight component.
 */
const ConversationLight = ({ open, onClose, action, values, resource }) => {
	const [conversation, setConversation] = useState({});
	const [tab, setTab] = useState(0);
	const [fullScreenTab, setFullScreenTab] = useState(0);
	const [visualiseConversationLoading, setVisualiseConversationLoading] = useState(true);
	const [fullScreen, setFullScreen] = useState(false);
	const [dictionary, setDictionary] = useState({});
	const [dictionarySkeleton, setDictionarySkeleton] = useState({});

	const dispatch = useDispatch();

	const isConversationAnalysed =
		!lod_.isNil(conversation?.analyse) && !lod_.isEmpty(conversation?.analyse);

	const analyseState = conversation?.analyseState;
	const stateStyle = ANALYSE_STATE_COLORS[analyseState] || ANALYSE_STATE_COLORS.created;

	const createdAt = formatDate(conversation?.createdAt);

	const tabs = action?.config?.tabs || [];

	const tabTabs = tabs.filter(t => t.scopes.includes("tab"));
	const tabMessage = tabs.filter(t => t.scopes.includes("message"));

	const hasTabs = !lod_.isEmpty(tabs);

	/**
	 * Get conversation by ID.
	 * @param {string} id - The ID of the conversation to fetch.
	 */
	const getConversation = async id => {
		const queryConversation = await new Promise(resolve => {
			dispatch(
				resourceActions.getResource(resource?.code, id, res => {
					resolve(res);
				})
			);
		});

		setConversation(queryConversation);
		setVisualiseConversationLoading(false);
	};

	useEffect(() => {
		if (open === true) {
			setConversation({});
			setVisualiseConversationLoading(true);
			setTab(0);

			const id = values?._id;

			if (!lod_.isNil(id) && !lod_.isEmpty(id)) {
				getConversation(id);
			}
			setDictionary({});
			setDictionarySkeleton({});
		} else {
			setConversation({});
			setVisualiseConversationLoading(true);
			setTab(0);
			setDictionary({});
			setDictionarySkeleton({});
		}
	}, [values, open]);

	useEffect(() => {
		if (open === true) {
			const codeDictionary = resource?.format?.analyse;
			if (!lod_.isNil(codeDictionary) && !lod_.isEmpty(codeDictionary) && isConversationAnalysed) {
				(async () => {
					const result = await queryDictionary(dispatch, codeDictionary);
					if (!lod_.isNil(result)) {
						setDictionary(result);
						setDictionarySkeleton(createDictionarySkeleton(result, conversation?.analyse || {}));
					}
				})();
			}
		} else {
			setDictionary({});
			setDictionarySkeleton({});
		}
	}, [conversation, resource, isConversationAnalysed, dispatch, open]);

	return (
		<Drawer open={open} onClose={onClose} anchor="right" sx={drawerStyles(fullScreen)}>
			<Card
				sx={{
					height: "100%",
					boxShadow: "none",
					borderRadius: 0,
					p: 0,
					display: "flex",
					flexDirection: "column",
					overflow: "hidden"
				}}
			>
				{visualiseConversationLoading === true ? (
					<MDBox
						display="flex"
						flexDirection="column"
						alignItems="center"
						justifyContent="center"
						sx={{ height: "100%", gap: 2 }}
					>
						<CircularProgress color="info" size={44} thickness={4} />
						<MDTypography variant="body2" color="secondary" fontSize="small">
							Chargement...
						</MDTypography>
					</MDBox>
				) : (
					<>
						{/* ─── Header ─── */}
						<MDBox
							display="flex"
							alignItems="center"
							justifyContent="space-between"
							sx={{
								px: 2.5,
								py: 1.5,
								borderBottom: "1px solid #f0f0f0"
							}}
						>
							{/* Left: Title + ID */}
							<MDBox display="flex" alignItems="center" gap={1.5}>
								<MDBox
									display="flex"
									alignItems="center"
									justifyContent="center"
									sx={{
										width: 38,
										height: 38,
										borderRadius: "10px",
										background: "linear-gradient(135deg, #1A73E8, #49a3f1)",
										flexShrink: 0
									}}
								>
									<Icon sx={{ color: "#fff", fontSize: "20px !important" }}>chat</Icon>
								</MDBox>
								<MDBox>
									<MDBox display="flex" alignItems="center">
										<MDTypography variant="h6" sx={{ fontWeight: 600 }}>
											Conversation
										</MDTypography>
										{/* Metadata pills */}
										<MDBox
											display="flex"
											flexWrap="wrap"
											alignItems="center"
											gap={0.75}
											sx={{ ml: 1 }}
										>
											{createdAt !== "" && <MetadataPill icon="calendar_today" label={createdAt} />}
										</MDBox>
									</MDBox>
									<MDBox display="flex" alignItems="center" gap={0.75} mt={0.25}>
										<MDTypography variant="caption" sx={{ color: "#9e9e9e", fontSize: "0.72rem" }}>
											{conversation.fID || "—"}
										</MDTypography>
										{!lod_.isNil(analyseState) && (
											<Chip
												size="small"
												label={analyseState}
												sx={{
													height: 20,
													fontSize: "0.65rem",
													fontWeight: 600,
													textTransform: "capitalize",
													backgroundColor: stateStyle.bg,
													color: stateStyle.color,
													borderRadius: "6px"
												}}
											/>
										)}
									</MDBox>
								</MDBox>
							</MDBox>

							{/* Right: Actions */}
							<MDBox display="flex" alignItems="center" gap={0.25}>
								<Tooltip placement="top" title={fullScreen ? "Réduire" : "Plein écran"}>
									<IconButton
										onClick={() => setFullScreen(prev => !prev)}
										size="small"
										sx={{ color: "#7b809a" }}
									>
										<Icon fontSize="small">{fullScreen ? "fullscreen_exit" : "fullscreen"}</Icon>
									</IconButton>
								</Tooltip>
								<Tooltip placement="top" title="Fermer">
									<IconButton onClick={onClose} size="small" sx={{ color: "#7b809a" }}>
										<Icon fontSize="small">close</Icon>
									</IconButton>
								</Tooltip>
							</MDBox>
						</MDBox>

						{/* ─── Tabs (non-fullscreen) ─── */}
						{!fullScreen && (
							<MDBox sx={{ px: 2.5, mt: 1 }}>
								<Tabs value={tab} onChange={(e, v) => setTab(v)}>
									<Tab label="Conversation" />
									{!hasTabs && <Tab label="Analyse" disabled={!isConversationAnalysed} />}
									{hasTabs && tabTabs.map((t, i) => <Tab key={i} label={t.name} />)}
								</Tabs>
							</MDBox>
						)}

						{/* ─── Content (non-fullscreen) ─── */}
						{!fullScreen && (
							<MDBox
								sx={{
									flex: 1,
									overflow: "hidden",
									display: "flex",
									flexDirection: "column"
								}}
							>
								{/* Conversation */}
								{tab === 0 && (
									<ConversationTab
										conversation={conversation}
										resource={resource}
										tabMessage={tabMessage}
										hasTabs={hasTabs}
									/>
								)}
								{/* Analyse v3.0 */}
								{tab === 1 && isConversationAnalysed && !hasTabs && (
									<Analyse
										conversation={conversation}
										resource={resource}
										dictionary={dictionary}
										dictionarySkeleton={dictionarySkeleton}
									/>
								)}
								{hasTabs && tab > 0 && <TabContent tab={tabTabs[tab - 1]} values={conversation} />}
							</MDBox>
						)}

						{/* ─── Content (fullscreen: side-by-side) ─── */}
						{fullScreen && (
							<MDBox display="flex" sx={{ flex: 1, overflow: "hidden" }}>
								{/* Left: Conversation */}
								<MDBox
									sx={{
										flex: 1,
										display: "flex",
										flexDirection: "column",
										borderRight: isConversationAnalysed ? "1px solid #f0f0f0" : "none",
										overflow: "hidden"
									}}
								>
									<MDBox
										display="flex"
										alignItems="center"
										gap={0.75}
										sx={{ px: 2.5, py: 1, borderBottom: "1px solid #f0f0f0" }}
									>
										<Icon sx={{ color: "#7b809a", fontSize: "18px !important" }}>
											chat_bubble_outline
										</Icon>
										<MDTypography variant="body2" fontWeight="medium" sx={{ fontSize: "0.82rem" }}>
											Conversation
										</MDTypography>
									</MDBox>
									<MDBox sx={{ flex: 1, overflow: "hidden" }}>
										<ConversationTab
											conversation={conversation}
											resource={resource}
											tabMessage={tabMessage}
											hasTabs={hasTabs}
										/>
									</MDBox>
								</MDBox>

								{/* Right: Analyse (only if analysed) */}
								{isConversationAnalysed && !hasTabs && (
									<MDBox
										sx={{
											flex: 1,
											display: "flex",
											flexDirection: "column",
											overflow: "hidden"
										}}
									>
										<MDBox
											display="flex"
											alignItems="center"
											gap={0.75}
											sx={{ px: 2.5, py: 1, borderBottom: "1px solid #f0f0f0" }}
										>
											<Icon sx={{ color: "#7b809a", fontSize: "18px !important" }}>analytics</Icon>
											<MDTypography
												variant="body2"
												fontWeight="medium"
												sx={{ fontSize: "0.82rem" }}
											>
												Analyse
											</MDTypography>
										</MDBox>
										<MDBox sx={{ flex: 1, overflow: "auto" }}>
											<Analyse
												conversation={conversation}
												resource={resource}
												dictionary={dictionary}
												dictionarySkeleton={dictionarySkeleton}
											/>
										</MDBox>
									</MDBox>
								)}
								{/* Tabs */}
								{hasTabs && (
									<MDBox
										sx={{
											flex: 1,
											display: "flex",
											flexDirection: "column",
											overflow: "hidden"
										}}
									>
										<Tabs value={fullScreenTab} onChange={(e, v) => setFullScreenTab(v)}>
											{tabTabs.map((t, i) => {
												return <Tab key={i} label={t.name} />;
											})}
										</Tabs>
										<TabContent tab={tabTabs[fullScreenTab]} values={conversation} />
									</MDBox>
								)}
							</MDBox>
						)}
					</>
				)}
			</Card>
		</Drawer>
	);
};

export default ConversationLight;
